import { config } from "../config.js";
import { cameraFor } from "./camera-modes.js";

// 試合の後のリプレイだけの演出カメラ(2026-10-31 オーナー要望「SENSEOFVOID のように俯瞰でかっこよく見たい」)。見た目だけ。
// 値は config.graphics.replayCameras。
// 見えてはいけない所が映らないように:
//   - カメラは自機の近くに留め、後ろは向かない(maxRadius・maxBack・minY で位置を抑える)
//   - 敵が前で出てくる・後ろで消えるのをポンと見せず、大きさを育てる・縮める(appearScale。index.js が使う)
// AUTO のカット割りは試合の秒(world.progress)と出来事だけで決めるので、同じ所を見ればいつも同じカットになる(シークしても同じ)。

const R = config.graphics.replayCameras;
export const REPLAY_CAMERA_MODES = R.order;

// 視点の名前(ボタンの表示)
export function replayCameraName(mode) {
  return (R[mode] || config.graphics.cameras[mode] || R.auto).name;
}

export function createReplayDirector() {
  let override = null; // { shot, until(試合の秒), side }

  // 出来事を見て、しばらく決まった視点に切り替える(AUTO のときだけ効く)
  function onEvents(events, world) {
    const on = R.director.onEvent;
    for (const e of events) {
      const rule = e.k === "kill" && e.big ? on.bigKill : on[e.k];
      if (rule) override = { shot: rule.shot, until: world.progress + rule.seconds, from: world.progress };
    }
  }

  // mode の視点でのカメラ(cameraFor と同じ形)を返す
  function cameraAt(world, mode, baseFov, wide) {
    if (mode !== "auto") return shot(world, mode, baseFov, wide, 1, 0.5);
    const d = R.director;
    const t = world.progress;
    if (override && (t >= override.until || t < override.from)) override = null; // 終わった・シークで前に戻った
    if (override) return shot(world, override.shot, baseFov, wide, 1, (t - override.from) / (override.until - override.from));
    // 一定の秒ごとにカットを変える。2つずつ進めるので、続けて同じ視点にはならない(視点が3つ以上のとき)
    const i = Math.floor(t / d.shotSeconds);
    const name = d.shots[(i * 2) % d.shots.length];
    return shot(world, name, baseFov, wide, i % 2 ? -1 : 1, (t % d.shotSeconds) / d.shotSeconds);
  }

  function clear() {
    override = null;
  }

  return { onEvents, cameraAt, clear };
}

// 1つの視点。sideSign: 真横を左右どちらから撮るか、k: カットの中の進み(0〜1。横へゆっくり流す)
function shot(world, name, baseFov, wide, sideSign, k) {
  const c = R[name];
  if (!c) return limit(cameraFor(world, name, baseFov, wide)); // 背後・斜め見下ろし・主観は遊ぶときと同じ
  const p = world.player;
  const s = world.scale || 1;
  const f = c.follow;
  const drift = (k - 0.5) * (c.sway || 0) * s;
  const fov = Math.min(config.scale.maxFov, baseFov + (s - 1) * config.scale.fovPerScale);
  return limit({
    x: p.x * f + ((c.side || 0) * sideSign + drift) * s,
    y: p.y * f + c.up * s,
    z: c.back * s,
    lx: p.x * f + drift * 0.3 * s,
    ly: p.y * f + c.lookY * s,
    lz: c.lookZ * s,
    fov,
    hidePlayer: false,
  });
}

// 見えてはいけない所が映らない範囲にカメラを抑える
function limit(cam) {
  const r = Math.hypot(cam.x, cam.y);
  if (r > R.maxRadius) {
    cam.x *= R.maxRadius / r;
    cam.y *= R.maxRadius / r;
  }
  cam.y = Math.max(R.minY, cam.y);
  cam.z = Math.min(R.maxBack, cam.z);
  return cam;
}

// 敵の見た目の大きさに掛ける割合(0〜1)。出てきた直後は育ち、後ろ・横で消える手前は縮む
// 消える位置は world.js の cleanup と同じ(後ろ: despawnBehindZ、横: 400m。どちらも倍率を掛ける)
export function appearScale(e, world) {
  const s = world.scale || 1;
  const grow = Math.min(1, e.age / R.growSeconds);
  const back = config.field.despawnBehindZ * s;
  const side = 400 * s;
  const shrinkBack = (back - e.z) / (back * R.shrinkBehind);
  const shrinkSide = (side - Math.abs(e.x)) / (side * 0.15);
  return grow * Math.min(1, Math.max(0, Math.min(shrinkBack, shrinkSide)));
}
